import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

export default function AlertsPerformanceChart({ refreshInterval = 300000 }) {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('execution_time');
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [availableAlerts, setAvailableAlerts] = useState([]);

  useEffect(() => {
    fetchAlerts();
    fetchPerformanceData();
    
    // Set up auto-refresh
    const intervalId = setInterval(() => {
      fetchPerformanceData();
    }, refreshInterval);
    
    return () => clearInterval(intervalId);
  }, [refreshInterval, selectedMetric, selectedPeriod, selectedAlerts]);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get('/api/alerts');
      setAvailableAlerts(response.data);
      
      // If no alerts are selected, select the first 3 by default
      if (selectedAlerts.length === 0 && response.data.length > 0) {
        setSelectedAlerts(response.data.slice(0, 3).map(alert => alert.id));
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Failed to load alerts for performance chart');
    }
  };

  const fetchPerformanceData = async () => {
    if (selectedAlerts.length === 0) return;
    
    setLoading(true);
    try {
      // Construct the time range based on selected period
      const endDate = new Date();
      let startDate = new Date();
      
      switch (selectedPeriod) {
        case 'day':
          startDate.setDate(startDate.getDate() - 1);
          break;
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(startDate.getMonth() - 3);
          break;
        default:
          startDate.setDate(startDate.getDate() - 7);
      }
      
      // Fetch performance data for each selected alert
      const alertDataPromises = selectedAlerts.map(async (alertId) => {
        const response = await axios.get(`/api/alerts/${alertId}/history`, {
          params: {
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
            limit: 100
          }
        });
        
        const alert = availableAlerts.find(a => a.id === alertId);
        return {
          alertId,
          alertName: alert ? alert.name : `Alert ${alertId}`,
          executions: response.data
        };
      });
      
      const alertsData = await Promise.all(alertDataPromises);
      setPerformanceData(alertsData);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const handleMetricChange = (e) => {
    setSelectedMetric(e.target.value);
  };

  const handlePeriodChange = (e) => {
    setSelectedPeriod(e.target.value);
  };

  const handleAlertToggle = (alertId) => {
    setSelectedAlerts(prev => {
      if (prev.includes(alertId)) {
        return prev.filter(id => id !== alertId);
      } else {
        return [...prev, alertId];
      }
    });
  };

  const getChartData = () => {
    if (!performanceData) return null;
    
    // Generate random colors for each alert
    const colors = [
      'rgba(75, 192, 192, 1)',
      'rgba(153, 102, 255, 1)',
      'rgba(255, 159, 64, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(255, 99, 132, 1)',
      'rgba(255, 206, 86, 1)'
    ];
    
    // Prepare datasets for the chart
    const datasets = performanceData.map((alertData, index) => {
      // Sort executions by date
      const sortedExecutions = [...alertData.executions].sort(
        (a, b) => new Date(a.run_start_time) - new Date(b.run_start_time)
      );
      
      // Extract the selected metric from each execution
      const data = sortedExecutions.map(execution => {
        switch (selectedMetric) {
          case 'execution_time':
            return execution.execution_time_seconds;
          case 'affected_nodes':
            return execution.affected_nodes_count;
          default:
            return execution.execution_time_seconds;
        }
      });
      
      // Extract labels (dates) for each execution
      const labels = sortedExecutions.map(execution => 
        new Date(execution.run_start_time).toLocaleString()
      );
      
      return {
        label: alertData.alertName,
        data,
        labels,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length].replace('1)', '0.2)'),
        tension: 0.4
      };
    });
    
    // Find all unique labels across all datasets
    const allLabels = new Set();
    datasets.forEach(dataset => {
      dataset.labels.forEach(label => allLabels.add(label));
    });
    
    // Convert to array and sort chronologically
    const sortedLabels = Array.from(allLabels).sort(
      (a, b) => new Date(a) - new Date(b)
    );
    
    // Normalize datasets to have the same labels
    const normalizedDatasets = datasets.map(dataset => {
      const normalizedData = [];
      
      sortedLabels.forEach(label => {
        const index = dataset.labels.indexOf(label);
        normalizedData.push(index !== -1 ? dataset.data[index] : null);
      });
      
      return {
        ...dataset,
        data: normalizedData
      };
    });
    
    return {
      labels: sortedLabels,
      datasets: normalizedDatasets
    };
  };

  const chartData = getChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: selectedMetric === 'execution_time' 
          ? 'Alert Execution Times' 
          : 'Affected Nodes Count'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += selectedMetric === 'execution_time'
                ? context.parsed.y + ' seconds'
                : context.parsed.y + ' nodes';
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: selectedMetric === 'execution_time' 
            ? 'Execution Time (seconds)' 
            : 'Affected Nodes Count'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Execution Date'
        }
      }
    }
  };

  if (loading && !performanceData) {
    return (
      <div className="bg-white shadow rounded p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded">
      <div className="bg-purple-l border-b border-gray-200 px-6 py-2 flex justify-between items-center">
        <h2 className="text-white font-bold tracking-wider uppercase text-md">Performance Trends</h2>
        <div className="flex items-center space-x-2">
          <select
            value={selectedMetric}
            onChange={handleMetricChange}
            className="bg-white text-purple-600 text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
          >
            <option value="execution_time">Execution Time</option>
            <option value="affected_nodes">Affected Nodes</option>
          </select>
          <select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            className="bg-white text-purple-600 text-xs font-bold py-1 px-2 rounded focus:outline-none focus:shadow-outline"
          >
            <option value="day">Last 24 Hours</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last 90 Days</option>
          </select>
        </div>
      </div>
      
      <div className="p-4">
        <div className="flex flex-wrap gap-2 mb-4">
          {availableAlerts.map(alert => (
            <button
              key={alert.id}
              onClick={() => handleAlertToggle(alert.id)}
              className={`text-xs py-1 px-2 rounded ${
                selectedAlerts.includes(alert.id)
                  ? 'bg-purple-500 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              {alert.name}
            </button>
          ))}
        </div>
        
        {chartData && selectedAlerts.length > 0 ? (
          <div className="h-64">
            <Line data={chartData} options={chartOptions} />
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            {selectedAlerts.length === 0 
              ? 'Select at least one alert to view performance data'
              : 'No performance data available for the selected alerts'}
          </div>
        )}
        
        <div className="mt-4 text-xs text-gray-500">
          <p>
            {selectedMetric === 'execution_time' 
              ? 'This chart shows execution time trends for the selected alerts. Lower values indicate better performance.'
              : 'This chart shows the number of affected nodes for each alert execution. Higher values indicate more nodes were updated.'}
          </p>
        </div>
      </div>
    </div>
  );
} 